import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';

import { payrollApi } from '../../services/api';
import type { ClassShiftTypeChange, ShiftType } from '../../types';
import { centerDateKey } from '../../utils/geolocation';
import { formatDateKey } from '../../utils/money';
import { Button, DateInput, LoadingState, Select, StatusBadge, useToast } from '../ui';

// The type in effect today, if any.
export function currentShiftTypeChange(changes: ClassShiftTypeChange[], today = centerDateKey()) {
    return [...changes]
        .filter(change => change.effectiveFrom <= today)
        .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0] ?? null;
}

// Which shift type the class is paid as, over time (admin only). Changing it never touches past shifts.
export function ClassShiftType({ classId }: { classId: string }) {
    const { showToast } = useToast();
    const [changes, setChanges] = useState<ClassShiftTypeChange[] | null>(null);
    const [types, setTypes] = useState<ShiftType[]>([]);
    const [shiftTypeId, setShiftTypeId] = useState('');
    const [effectiveFrom, setEffectiveFrom] = useState(centerDateKey());
    const [saving, setSaving] = useState(false);
    const [confirmingId, setConfirmingId] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setChanges(null);
        Promise.all([payrollApi.getClassShiftTypes(classId), payrollApi.getShiftTypes()])
            .then(([changesResponse, typesResponse]) => {
                if (cancelled) return;
                setTypes(typesResponse.data.data);
                setChanges(changesResponse.data.data);
                const current = currentShiftTypeChange(changesResponse.data.data);
                setShiftTypeId(current?.shiftTypeId ?? typesResponse.data.data.find(type => type.isActive)?.id ?? '');
            })
            .catch((error: any) => {
                if (cancelled) return;
                showToast(error?.message || 'Không tải được loại ca của lớp.', 'error');
                setChanges([]);
            });
        return () => { cancelled = true; };
    }, [classId, showToast]);

    if (!changes) return <LoadingState title="Đang tải loại ca" />;

    const current = currentShiftTypeChange(changes);
    const ordered = [...changes].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
    const replacing = changes.some(change => change.effectiveFrom === effectiveFrom);

    const save = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!shiftTypeId || !effectiveFrom) return;
        setSaving(true);
        try {
            const response = await payrollApi.setClassShiftType(classId, { shiftTypeId, effectiveFrom });
            setChanges(response.data.data);
            showToast(`Đã đổi loại ca từ ${formatDateKey(effectiveFrom)}.`, 'success');
        } catch (error: any) {
            showToast(error?.message || 'Lưu loại ca thất bại.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (change: ClassShiftTypeChange) => {
        setSaving(true);
        try {
            const response = await payrollApi.deleteClassShiftType(classId, change.id);
            setChanges(response.data.data);
            setConfirmingId(null);
            showToast('Đã xoá thay đổi loại ca.', 'success');
        } catch (error: any) {
            showToast(error?.message || 'Xoá thất bại.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-3 text-sm">
            <p className="text-ink-700">
                Đang tính lương theo: <strong className="text-ink-900">{current?.shiftType.name ?? 'chưa có loại ca'}</strong>
                {!current ? <span className="text-warning-700"> — các ca của lớp sẽ chưa được tính tiền.</span> : null}
            </p>
            {ordered.length > 0 ? (
                <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
                    {ordered.map(change => (
                        <li key={change.id} className="flex items-center justify-between gap-2 px-3 py-2">
                            <div className="min-w-0">
                                <p className="font-medium text-ink-900">{change.shiftType.name}</p>
                                <p className="text-xs text-ink-500">
                                    {change.effectiveFrom <= '2000-01-01' ? 'Từ đầu' : `Từ ${formatDateKey(change.effectiveFrom)}`}
                                    {change.effectiveFrom > centerDateKey() ? ' · sắp áp dụng' : ''}
                                </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                                {current?.id === change.id ? <StatusBadge tone="success" icon={null}>Đang áp dụng</StatusBadge> : null}
                                {confirmingId === change.id ? (
                                    <>
                                        <Button size="sm" variant="danger" loading={saving} onClick={() => remove(change)}>Xoá</Button>
                                        <Button size="sm" variant="ghost" disabled={saving} onClick={() => setConfirmingId(null)}>Huỷ</Button>
                                    </>
                                ) : (
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8"
                                        aria-label={`Xoá loại ca ${change.shiftType.name} từ ${formatDateKey(change.effectiveFrom)}`}
                                        icon={<Trash2 className="h-4 w-4" />}
                                        onClick={() => setConfirmingId(change.id)}
                                    />
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            ) : null}
            <form onSubmit={save} className="grid gap-3 rounded-lg border border-ink-200 bg-ink-50 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,11rem)_auto] sm:items-end">
                <Select label="Loại ca" value={shiftTypeId} onChange={event => setShiftTypeId(event.target.value)} disabled={saving}>
                    {types.filter(type => type.isActive).map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                </Select>
                <DateInput label="Áp dụng từ" value={effectiveFrom} onChange={setEffectiveFrom} clearable={false} disabled={saving} />
                <Button type="submit" loading={saving}>{replacing ? 'Cập nhật' : 'Đổi loại ca'}</Button>
                <p className="text-xs text-ink-500 sm:col-span-3">
                    Các buổi trước ngày áp dụng vẫn tính theo loại ca cũ.
                </p>
            </form>
        </div>
    );
}
